import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Container } from './ui/Container';
import { X, Mail, Copy, Check } from 'lucide-react';
type ModalKey = 'privacy' | 'terms' | 'contact' | null;
const CONTACT_EMAIL = 'contact@glowliftessence.com';
export function Footer() {
  const [openModal, setOpenModal] = useState<ModalKey>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (openModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [openModal]);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenModal(null);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);
  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT_EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };
  return <>
      <footer className="bg-gray-900 text-gray-400 py-12 border-t border-gray-800" data-id="element-81">
        <Container data-id="element-82">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8" data-id="element-83">
            <div className="flex items-center space-x-2 mb-4 md:mb-0" data-id="element-84">
              <img src="https://cdn.magicpatterns.com/uploads/j7d4EdH3Rp1gzCoto8E26y/imgi_1_1746975280-SnoreStop™.png" alt="SnoreStop Logo" className="h-8 brightness-0 invert" data-id="element-85" />
            </div>
            <div className="flex space-x-6 text-sm" data-id="element-86">
              <button onClick={() => setOpenModal('privacy')} className="hover:text-white transition-colors" data-id="element-87">
                Privacy Policy
              </button>
              <button onClick={() => setOpenModal('terms')} className="hover:text-white transition-colors" data-id="element-88">
                Terms of Service
              </button>
              <button onClick={() => setOpenModal('contact')} className="hover:text-white transition-colors" data-id="element-89">
                Contact Us
              </button>
            </div>
          </div>

          <div className="text-xs text-gray-500 text-center max-w-3xl mx-auto leading-relaxed" data-id="element-90">
            <p className="mb-4" data-id="element-91">
              SnoreStop™ is designed to help reduce snoring by positioning the
              jaw to open airways. Individual results may vary. This product is
              not intended to diagnose, treat, cure, or prevent any disease.
              Consult a healthcare professional if you have concerns about sleep
              apnea or other sleep disorders.
            </p>
            <p data-id="element-92">
              &copy; {new Date().getFullYear()} SnoreStop™. All rights
              reserved.
            </p>
          </div>
        </Container>
      </footer>

      <AnimatePresence data-id="element-93">
        {openModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpenModal(null)} role="dialog" aria-modal="true" data-id="element-94">
            <motion.div initial={{
          opacity: 0,
          scale: 0.96,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.96,
          y: 10
        }} transition={{
          duration: 0.2
        }} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" data-id="element-95">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0" data-id="element-96">
                <h2 className="text-lg font-serif text-gray-900" data-id="element-97">
                  {openModal === 'privacy' && 'Privacy Policy'}
                  {openModal === 'terms' && 'Terms of Service'}
                  {openModal === 'contact' && 'Contact Us'}
                </h2>
                <button onClick={() => setOpenModal(null)} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors" aria-label="Close" data-id="element-98">
                  <X className="w-5 h-5" data-id="element-99" />
                </button>
              </div>

              <div className="px-6 py-6 overflow-y-auto text-gray-600 leading-relaxed text-sm space-y-4" data-id="element-100">
                {openModal === 'contact' && <div className="space-y-6 py-4 text-center" data-id="element-101">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mx-auto" data-id="element-102">
                      <Mail className="w-8 h-8 text-[#1E40AF]" data-id="element-103" />
                    </div>
                    <div className="space-y-2" data-id="element-104">
                      <h3 className="text-xl font-serif text-gray-900" data-id="element-105">
                        We'd love to hear from you
                      </h3>
                      <p className="text-gray-600" data-id="element-106">
                        Questions about your order, shipping, or how SnoreStop™
                        works? Reach out and we'll get back to you within 24
                        hours.
                      </p>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-between gap-3 max-w-md mx-auto" data-id="element-107">
                      <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#1E40AF] font-medium truncate hover:underline" data-id="element-108">
                        {CONTACT_EMAIL}
                      </a>
                      <button onClick={copyEmail} className="flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 bg-white px-3 py-1.5 rounded-full border border-gray-200 transition-colors flex-shrink-0" data-id="element-109">
                        {copied ? <>
                            <Check className="w-3.5 h-3.5 text-green-600" data-id="element-110" />
                            Copied
                          </> : <>
                            <Copy className="w-3.5 h-3.5" data-id="element-111" />
                            Copy
                          </>}
                      </button>
                    </div>

                    <a href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center justify-center bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-bold px-6 py-3 rounded-full transition-colors" data-id="element-112">
                      Send us an email
                    </a>
                  </div>}

                {openModal === 'privacy' && <>
                    <p data-id="element-113">
                      <strong data-id="element-114">Last Updated:</strong>{' '}
                      {new Date().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                    </p>
                    <p data-id="element-115">
                      We respect your privacy and are committed to protecting
                      your personal information. This policy explains what we
                      collect, how we use it, and the choices you have.
                    </p>
                    <h3 className="font-semibold text-gray-900 pt-2" data-id="element-116">
                      Information we collect
                    </h3>
                    <p data-id="element-117">
                      When you place an order, we collect your name, shipping
                      address, email address, and payment information necessary
                      to fulfill your purchase. We may also collect basic
                      analytics data about how you use our website.
                    </p>
                    <h3 className="font-semibold text-gray-900 pt-2" data-id="element-118">
                      How we use your information
                    </h3>
                    <p data-id="element-119">
                      Your information is used to process your order, ship your
                      product, send order updates, and respond to your support
                      requests. We do not sell your personal information to
                      third parties.
                    </p>
                    <h3 className="font-semibold text-gray-900 pt-2" data-id="element-120">
                      Payment processing
                    </h3>
                    <p data-id="element-121">
                      Payments are processed by trusted third-party providers.
                      We do not store your full payment card details on our
                      servers.
                    </p>
                    <h3 className="font-semibold text-gray-900 pt-2" data-id="element-122">
                      Your rights
                    </h3>
                    <p data-id="element-123">
                      You can request a copy of your data, request corrections,
                      or ask us to delete your information at any time by
                      contacting{' '}
                      <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#1E40AF] hover:underline" data-id="element-124">
                        {CONTACT_EMAIL}
                      </a>
                      .
                    </p>
                    <h3 className="font-semibold text-gray-900 pt-2" data-id="element-125">
                      Questions
                    </h3>
                    <p data-id="element-126">
                      If you have any questions about this privacy policy,
                      please email{' '}
                      <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#1E40AF] hover:underline" data-id="element-127">
                        {CONTACT_EMAIL}
                      </a>
                      .
                    </p>
                  </>}

                {openModal === 'terms' && <>
                    <p data-id="element-128">
                      <strong data-id="element-129">Last Updated:</strong>{' '}
                      {new Date().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                    </p>
                    <p data-id="element-130">
                      By using this website and purchasing SnoreStop™, you
                      agree to the following terms.
                    </p>
                    <h3 className="font-semibold text-gray-900 pt-2" data-id="element-131">
                      Use of the product
                    </h3>
                    <p data-id="element-132">
                      SnoreStop™ is a wearable mouthpiece intended to help
                      support quieter sleep. It is not a medical device and is
                      not intended to diagnose, treat, cure, or prevent any
                      disease, including sleep apnea. If you have concerns about
                      your sleep or breathing, please consult a healthcare
                      professional.
                    </p>
                    <h3 className="font-semibold text-gray-900 pt-2" data-id="element-133">
                      60-Night Money Back Guarantee
                    </h3>
                    <p data-id="element-134">
                      If you are not satisfied with SnoreStop™ within 60 nights
                      of receiving your order, contact us for a full refund.
                      Refunds are issued to the original payment method.
                    </p>
                    <h3 className="font-semibold text-gray-900 pt-2" data-id="element-135">
                      Shipping
                    </h3>
                    <p data-id="element-136">
                      Most orders ship within 24 hours. Delivery times vary by
                      destination. We are not responsible for delays caused by
                      carriers or customs.
                    </p>
                    <h3 className="font-semibold text-gray-900 pt-2" data-id="element-137">
                      Intellectual property
                    </h3>
                    <p data-id="element-138">
                      All content on this website, including text, images, and
                      branding, is the property of SnoreStop™ and may not be
                      reproduced without permission.
                    </p>
                    <h3 className="font-semibold text-gray-900 pt-2" data-id="element-139">
                      Limitation of liability
                    </h3>
                    <p data-id="element-140">
                      To the maximum extent permitted by law, SnoreStop™ is not
                      liable for indirect, incidental, or consequential damages
                      arising from the use of this website or product.
                    </p>
                    <h3 className="font-semibold text-gray-900 pt-2" data-id="element-141">
                      Contact
                    </h3>
                    <p data-id="element-142">
                      For any questions about these terms, contact{' '}
                      <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#1E40AF] hover:underline" data-id="element-143">
                        {CONTACT_EMAIL}
                      </a>
                      .
                    </p>
                  </>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}